// 저장한 보기 (design 02 3.6): a source (a tensor's name or a formula) that
// the engine calculates on this device, its dimensions placed as rows
// (nested), columns, fixed to an item or summed/averaged (pivot.ts), and a
// chart. The server keeps each view's settings as they are (views: a name
// per workbook); in a workbook open to the family anyone makes, changes and
// deletes them, as the defined names. What is moved here stays on this
// screen until 저장 (the others see the saved one).
//   - The dimensions are chips: dragged between the places (PC), or put
//     with each chip's menu (phone; a select).
//   - When the source is an input tensor's name and nothing is summed, its
//     cells are typed in place (tensors.ts coordOfNames) and sent as in the
//     tensor's editor.
//   - /views/<id> is a view; the list is the 보기 tab 저장한 보기.
import { type DragEvent, type FormEvent, type KeyboardEvent, type ReactNode, useEffect, useMemo, useState } from 'react'

import { errorText } from '../../api/client'
import { hasFinePointer, isComposing } from '../../lib/ime'
import { ErrorText, Field, Icon, IconButton, Sheet, Spinner } from '../../ui/common'
import { goBack, navigate } from '../../ui/router'
import { showToast } from '../../ui/toast'
import type { Id, TensorDef, TensorSavedView, TensorViewConfig } from '../api'
import { type Shape as ChartShape, chartShapes } from '../chart'
import { engine } from '../engine'
import { monthOf } from '../format'
import type { GridStore } from '../grid/store'
import { parseTsv } from '../grid/tsv'
import { useGrid } from '../grid/useGrid'
import {
  type Axis,
  type AxisRole,
  type ChartKind,
  type Pivot,
  type PivotValue,
  type Source,
  ROLES,
  ROLE_LABEL,
  axesOf,
  chartData,
  chartDims,
  chartKind,
  configAxes,
  headerSpans,
  heatShapes,
  moveAxis,
  pivot,
  pivotCsv,
  rowHeads,
  savedViews,
  sourceOf,
} from '../pivot'
import { coordOfNames, inputTensors } from '../tensors'
import type { Current } from './common'
import { EntryCell, type TypedCell, markPending, sendEntries } from './Tensors'

const CHARTS: { value: ChartKind; label: string }[] = [
  { value: 'auto', label: '자동' },
  { value: 'line', label: '선' },
  { value: 'bar', label: '막대' },
  { value: 'heat', label: '열지도' },
  { value: 'groupbar', label: '묶은 막대' },
  { value: 'none', label: '그림 없음' },
]

/** small charts at most (the third dimension's items) */
const SMALL_MAX = 12

/** A typed source: no "=" in front, no space around. */
function cleanSource(text: string): string {
  return text.trim().replace(/^=\s*/, '')
}

/** Why a view's name is refused, or null. */
function viewNameProblem(name: string, views: TensorSavedView[], except = 0): string | null {
  const n = name.trim()
  if (n === '') return '이름을 적어 주세요.'
  if ([...n].length > 30) return '이름은 30자까지입니다.'
  if (/^\d+$/.test(n)) return '숫자만으로 된 이름은 쓸 수 없습니다(주소에서 번호로 읽힙니다).'
  if (views.some((v) => v.id !== except && v.name.toLowerCase() === n.toLowerCase())) return '같은 이름의 보기가 있습니다.'
  return null
}

/** Settings compared as values (the keys' order aside). */
function sameConfig(a: TensorViewConfig, b: TensorViewConfig): boolean {
  const canon = (v: unknown): unknown =>
    Array.isArray(v)
      ? v.map(canon)
      : v !== null && typeof v === 'object'
        ? Object.fromEntries(
            Object.keys(v)
              .sort()
              .map((k) => [k, canon((v as Record<string, unknown>)[k])]),
          )
        : v
  return JSON.stringify(canon(a)) === JSON.stringify(canon(b))
}

/** The names a source may be: the built-in tensors, the input ones, the defined names. */
function sourceNames(cur: Current, store: GridStore): string[] {
  return [
    '지출',
    '수입',
    '예산',
    ...inputTensors(cur.book?.tensors).map((t) => t.name),
    ...store.model.nameList().map((n) => n.name),
  ]
}

function SourceList({ id, names }: { id: string; names: string[] }) {
  return (
    <datalist id={id}>
      {names.map((n) => (
        <option key={n} value={n} />
      ))}
    </datalist>
  )
}

type Answer = { src: Source } | { error: string } | null

/** The engine's answer for a source, again when the workbook's data change. */
function useSource(store: GridStore, source: string): Answer {
  const [answer, setAnswer] = useState<Answer>(null)
  useEffect(() => {
    let live = true
    const t = setTimeout(() => {
      store
        .evaluate(['=' + source])
        .then(([a]) => {
          if (!live) return
          if (!a?.ok) return setAnswer({ error: a?.error ?? '#CALC!' })
          const s = sourceOf(a.value)
          setAnswer('error' in s ? s : { src: s })
        })
        .catch((e) => live && setAnswer({ error: errorText(e) }))
    }, 80)
    return () => {
      live = false
      clearTimeout(t)
    }
    // the store's version is a change of its data
  }, [store, store.version, source])
  return answer
}

// ---- the list ----

function NewViewSheet({ cur, store, open, onClose }: { cur: Current; store: GridStore; open: boolean; onClose: () => void }) {
  const [name, setName] = useState('')
  const [source, setSource] = useState('지출')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    const why = viewNameProblem(name, savedViews(cur.book?.views))
    if (why) return setError(why)
    const src = cleanSource(source)
    if (src === '') return setError('원본을 적어 주세요.')
    setBusy(true)
    setError(null)
    try {
      const v = await engine.addView(cur.workbook.id, name.trim(), { source: src, chart: 'auto' })
      setName('')
      onClose()
      navigate(`/views/${v.id}`)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="새 보기">
      <form className="t-form" onSubmit={(e) => void submit(e)}>
        <Field label="이름" hint="예: 월별 식비, 사람별 용돈">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} autoComplete="off" />
        </Field>
        <Field label="원본" hint="텐서의 이름이나 수식 (예: 지출, 지출[분류=&quot;식비&quot;], 목표 - 지출)">
          <input
            className="t-formula"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            list="t-view-sources"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <SourceList id="t-view-sources" names={sourceNames(cur, store)} />
        <ErrorText text={error} />
        <button type="submit" className="primary" disabled={busy || name.trim() === ''}>
          만들기
        </button>
      </form>
    </Sheet>
  )
}

/** The 보기 tab 저장한 보기. */
export function SavedViewList({ cur }: { cur: Current }) {
  const store = useGrid(cur.workbook.id)
  const [making, setMaking] = useState(false)
  const views = savedViews(cur.book?.views)
  return (
    <section className="card">
      {cur.book?.loading && views.length === 0 && <Spinner />}
      {views.length === 0 && !cur.book?.loading && (
        <p className="muted small">
          텐서나 수식을 행·열로 펼쳐 놓고 이름을 붙여 둡니다. {cur.workbook.visibility === 'family' ? '가족 모두가 보고 고칩니다.' : ''}
        </p>
      )}
      <ul className="list t-tensors t-saved-views">
        {views.map((v) => (
          <li key={v.id} className="t-tensor">
            <button type="button" className="t-tensor-main" onClick={() => navigate(`/views/${v.id}`)}>
              <span className="t-tensor-name">{v.name}</span>
              <span className="t-tensor-formula">={v.config.source}</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="secondary" onClick={() => setMaking(true)}>
        새 보기
      </button>
      <NewViewSheet cur={cur} store={store} open={making} onClose={() => setMaking(false)} />
    </section>
  )
}

// ---- placing the dimensions ----

const PLACES: { key: 'row' | 'col' | 'fix' | 'agg'; label: string; role: AxisRole }[] = [
  { key: 'row', label: '행', role: 'row' },
  { key: 'col', label: '열', role: 'col' },
  { key: 'fix', label: '고정', role: 'fix' },
  { key: 'agg', label: '합치기', role: 'sum' },
]

const placeOf = (r: AxisRole) => (r === 'sum' || r === 'mean' ? 'agg' : r)

function Places({ src, axes, onChange }: { src: Source; axes: Axis[]; onChange: (axes: Axis[]) => void }) {
  const [over, setOver] = useState<string | null>(null)
  const fine = hasFinePointer()
  const drop = (role: AxisRole, before?: string) => (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOver(null)
    const name = e.dataTransfer.getData('text/plain')
    const a = axes.find((x) => x.name === name)
    if (!a) return
    // into 합치기: an averaged one stays averaged
    onChange(moveAxis(axes, name, role === 'sum' && a.role === 'mean' ? 'mean' : role, before))
  }
  return (
    <div className="t-places">
      {PLACES.map((p) => {
        const here = axes.filter((a) => placeOf(a.role) === p.key)
        return (
          <div
            key={p.key}
            className={`t-place${over === p.key ? ' over' : ''}`}
            role="group"
            aria-label={p.label}
            onDragOver={(e) => {
              e.preventDefault()
              setOver(p.key)
            }}
            onDragLeave={() => setOver((o) => (o === p.key ? null : o))}
            onDrop={drop(p.role)}
          >
            <span className="t-place-label">{p.label}</span>
            {here.map((a) => {
              const items = src.dims.find((d) => d.name === a.name)?.items ?? []
              return (
                <span
                  key={a.name}
                  className="t-chip"
                  draggable={fine}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', a.name)
                    e.dataTransfer.effectAllowed = 'move'
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={drop(p.role, a.name)}
                >
                  <span className="t-chip-name">{a.name}</span>
                  <select
                    aria-label={`${a.name}의 자리`}
                    value={a.role}
                    onChange={(e) => onChange(moveAxis(axes, a.name, e.target.value as AxisRole))}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                  {a.role === 'fix' && (
                    <select
                      aria-label={`${a.name}의 항목`}
                      value={a.item ?? ''}
                      onChange={(e) => onChange(axes.map((x) => (x.name === a.name ? { ...x, item: e.target.value } : x)))}
                    >
                      {items.map((it) => (
                        <option key={it} value={it}>
                          {it}
                        </option>
                      ))}
                    </select>
                  )}
                </span>
              )
            })}
            {here.length === 0 && <span className="muted small">{fine ? '끌어다 놓기' : '없음'}</span>}
          </div>
        )
      })}
    </div>
  )
}

// ---- the table ----

function valueText(v: PivotValue): string {
  if (v === null) return ''
  if (typeof v === 'string') return v
  return v.toLocaleString('ko-KR', { maximumFractionDigits: Number.isInteger(v) ? 0 : 2 })
}

function PivotTable({ p, cell }: { p: Pivot; cell: (r: number, c: number, v: PivotValue) => ReactNode }) {
  const lead = Math.max(1, p.rowDims.length)
  const heads = rowHeads(p.rows)
  return (
    <div className="t-scroll">
      <table className="t-table t-pivot">
        <thead>
          {p.colDims.length === 0 ? (
            <tr>
              {p.rowDims.length ? p.rowDims.map((d) => <th key={d}>{d}</th>) : <th />}
              <th className="num">값</th>
            </tr>
          ) : (
            p.colDims.map((name, level) => (
              <tr key={name}>
                {level === p.colDims.length - 1 && p.rowDims.length ? (
                  p.rowDims.map((d, i) => <th key={d}>{i === lead - 1 ? `${d} \\ ${name}` : d}</th>)
                ) : (
                  <th colSpan={lead} className="t-pivot-dim">
                    {name}
                  </th>
                )}
                {headerSpans(p.cols, level).map((s) => (
                  <th key={s.at} colSpan={s.n} className="num">
                    {s.label}
                  </th>
                ))}
              </tr>
            ))
          )}
        </thead>
        <tbody>
          {p.rows.map((r, i) => (
            <tr key={r.join('\u0000') || i}>
              {p.rowDims.length ? (
                heads[i]!.map((x, k) => (
                  <th key={k} scope="row">
                    {x}
                  </th>
                ))
              ) : (
                <th scope="row" />
              )}
              {p.cols.map((_, j) => (
                <td key={j} className={`num${typeof p.cells[i]![j] === 'string' ? ' danger' : ''}`}>
                  {cell(i, j, p.cells[i]![j] ?? null)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---- the chart ----

function ShapesSvg({ shapes, w, h, label }: { shapes: ChartShape[]; w: number; h: number; label: string }) {
  return (
    <svg className="t-chart t-view-chart" viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label}>
      {shapes.map((sh, i) => {
        const a = sh.attrs as Record<string, string | number>
        if (sh.kind === 'text')
          return (
            <text key={i} {...a}>
              {sh.text}
            </text>
          )
        if (sh.kind === 'rect') return <rect key={i} {...a} />
        if (sh.kind === 'line') return <line key={i} {...a} />
        if (sh.kind === 'circle') return <circle key={i} {...a} />
        return <path key={i} {...a} />
      })}
    </svg>
  )
}

function ViewChart({ src, axes, kind, title }: { src: Source; axes: Axis[]; kind: ChartKind | undefined; title: string }) {
  const [paged, setPaged] = useState<Record<string, string>>({})
  const dims = chartDims(axes)
  const k = chartKind(kind, dims, src)
  if (k === 'none') return null
  const itemsOf = (name: string) => src.dims.find((d) => d.name === name)?.items ?? []
  const draw = (pg: Record<string, string>, w: number, h: number) => {
    const data = chartData(src, axes, pg)
    return k === 'heat' ? heatShapes(data, w, h) : chartShapes(k === 'line' ? 'line' : 'bar', data, w, h)
  }
  const small = dims[2]
  const sliders = dims.slice(3)
  return (
    <div className="card t-view-charts">
      {sliders.map((name) => {
        const items = itemsOf(name)
        const at = Math.max(0, items.indexOf(paged[name] ?? ''))
        return (
          <label key={name} className="t-slider">
            <span>
              {name}: <strong>{items[at]}</strong>
            </span>
            <input
              type="range"
              min={0}
              max={Math.max(0, items.length - 1)}
              value={at}
              onChange={(e) => setPaged((p) => ({ ...p, [name]: items[Number(e.target.value)] ?? '' }))}
            />
          </label>
        )
      })}
      {small === undefined ? (
        <ShapesSvg shapes={draw(paged, 640, 260)} w={640} h={260} label={`${title} 그림`} />
      ) : (
        <>
          <div className="t-small-charts">
            {itemsOf(small)
              .slice(0, SMALL_MAX)
              .map((item) => (
                <figure key={item}>
                  <figcaption className="small">
                    {small}: {item}
                  </figcaption>
                  <ShapesSvg shapes={draw({ ...paged, [small]: item }, 360, 200)} w={360} h={200} label={`${title} ${item} 그림`} />
                </figure>
              ))}
          </div>
          {itemsOf(small).length > SMALL_MAX && <p className="muted small">처음 {SMALL_MAX}개만 그렸습니다. 나머지는 {small}을(를) 고정해 보세요.</p>}
        </>
      )}
    </div>
  )
}

// ---- one view ----

function ViewSettings({ cur, view, onClose }: { cur: Current; view: TensorSavedView; onClose: () => void }) {
  const [name, setName] = useState(view.name)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function rename(e: FormEvent) {
    e.preventDefault()
    if (name.trim() === view.name) return onClose()
    const why = viewNameProblem(name, savedViews(cur.book?.views), view.id)
    if (why) return setError(why)
    setBusy(true)
    try {
      await engine.updateView(cur.workbook.id, view.id, { name: name.trim(), version: view.version })
      onClose()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!confirm(`보기 "${view.name}"을(를) 지울까요? ${cur.workbook.visibility === 'family' ? '가족 모두의 목록에서 사라집니다.' : ''}`)) return
    setBusy(true)
    try {
      await engine.deleteView(cur.workbook.id, view.id)
      onClose()
      navigate('/views', { replace: true })
    } catch (err) {
      setError(errorText(err))
      setBusy(false)
    }
  }

  const address = `${location.origin}/w/${cur.workbook.id}/v/${view.id}`
  return (
    <form className="t-form" onSubmit={(e) => void rename(e)}>
      <Field label="이름">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} autoComplete="off" />
      </Field>
      <Field label="이 보기의 주소" hint="통합 문서를 볼 수 있는 가족이 이 주소로 바로 엽니다.">
        <input value={address} readOnly onFocus={(e) => e.currentTarget.select()} />
      </Field>
      <ErrorText text={error} />
      <button type="submit" className="primary" disabled={busy}>
        이름 바꾸기
      </button>
      <button type="button" className="danger-btn" disabled={busy} onClick={() => void remove()}>
        보기 지우기
      </button>
    </form>
  )
}

function download(name: string, text: string): void {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }))
  a.download = `${name.replace(/[\\/:*?"<>|]/g, '_') || 'tensor'}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
}

/** The input tensor a source names (its cells are typed in the view), if any. */
function inputOf(cur: Current, source: string): TensorDef | undefined {
  const s = source.trim().toLowerCase()
  return inputTensors(cur.book?.tensors).find((t) => t.name.toLowerCase() === s)
}

function ViewEditor({ cur, view }: { cur: Current; view: TensorSavedView }) {
  const store = useGrid(cur.workbook.id)
  const [draft, setDraft] = useState<TensorViewConfig | null>(null)
  const [sourceText, setSourceText] = useState<string | null>(null)
  const [settings, setSettings] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pending, setPending] = useState<Set<string>>(() => new Set())
  const config = draft ?? view.config
  const answer = useSource(store, config.source)
  const src = answer && 'src' in answer ? answer.src : null
  const thisMonth = monthOf(Date.now())
  const axes = useMemo(() => (src ? axesOf(src, config, thisMonth) : []), [src, config, thisMonth])
  const table = useMemo(() => (src ? pivot(src, axes) : null), [src, axes])
  const changed = draft !== null && !sameConfig(draft, view.config)
  const tensor = inputOf(cur, config.source)
  const editable = tensor && table?.places ? tensor : null
  const items = cur.book?.items ?? {}
  const entries = editable ? (cur.book?.entries[editable.id] ?? {}) : {}

  const change = (next: Partial<TensorViewConfig>) => setDraft({ ...config, ...next })
  const place = (next: Axis[]) => change(configAxes(next))
  const applySource = () => {
    if (sourceText === null) return
    const s = cleanSource(sourceText)
    setSourceText(null)
    if (s !== '' && s !== config.source) change({ source: s })
  }

  async function save() {
    if (!draft) return
    setSaving(true)
    try {
      await engine.updateView(cur.workbook.id, view.id, { config: draft, version: view.version })
      setDraft(null)
    } catch (e) {
      showToast(errorText(e))
    } finally {
      setSaving(false)
    }
  }

  const coordAt = (r: number, c: number): string | null => {
    const at = table?.places?.[r]?.[c]
    return editable && at ? coordOfNames(editable, at, items, cur.book?.dims) : null
  }
  const send = (cells: TypedCell[]) => (editable ? sendEntries(cur.workbook.id, editable.id, cells, markPending(setPending)) : Promise.resolve())
  const paste = (r0: number, c0: number) => (text: string) => {
    const cells: TypedCell[] = []
    parseTsv(text).forEach((line, i) =>
      line.forEach((t, j) => {
        const coord = coordAt(r0 + i, c0 + j)
        if (coord) cells.push({ coord, text: t, entry: entries[coord] })
      }),
    )
    void send(cells)
    return true
  }

  return (
    <div className="page t-page t-wide-page t-saved-view">
      <div className="t-editor-head">
        <IconButton label="보기 목록" onClick={() => goBack('/views')}>
          <Icon name="back" />
        </IconButton>
        <h3 className="grow">{view.name}</h3>
        {table && !table.tooBig && (
          <IconButton label="CSV로 내려받기" onClick={() => download(view.name, pivotCsv(table))}>
            <Icon name="download" />
          </IconButton>
        )}
        <IconButton label="보기 설정" onClick={() => setSettings(true)}>
          <Icon name="gear" />
        </IconButton>
      </div>
      <div className="t-layout">
        <label className="t-source">
          원본 =
          <input
            className="t-formula"
            aria-label="원본"
            value={sourceText ?? config.source}
            list="t-view-sources"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setSourceText(e.target.value)}
            onBlur={applySource}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              // Enter while a syllable is being composed is not the end (design 8.7)
              if (e.key !== 'Enter' || isComposing(e)) return
              e.preventDefault()
              applySource()
            }}
          />
        </label>
        <SourceList id="t-view-sources" names={sourceNames(cur, store)} />
        <label>
          그림
          <select value={config.chart ?? 'auto'} onChange={(e) => change({ chart: e.target.value as ChartKind })}>
            {CHARTS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        {changed && (
          <span className="t-save-bar">
            <button type="button" className="primary" disabled={saving} onClick={() => void save()}>
              저장
            </button>
            <button type="button" className="secondary" disabled={saving} onClick={() => setDraft(null)}>
              되돌리기
            </button>
          </span>
        )}
      </div>
      {answer === null && <Spinner />}
      {answer && 'error' in answer && <p className="danger">원본을 계산할 수 없습니다: {answer.error}</p>}
      {src && src.dims.length > 0 && <Places src={src} axes={axes} onChange={place} />}
      {src && table && table.tooBig && (
        <p className="muted empty-note">칸이 너무 많습니다. 차원을 더 고정하거나 합쳐 주세요.</p>
      )}
      {src && table && !table.tooBig && (
        <>
          <ViewChart src={src} axes={axes} kind={config.chart} title={view.name} />
          <PivotTable
            p={table}
            cell={(r, c, v) => {
              const coord = coordAt(r, c)
              if (!coord) return valueText(v)
              return (
                <EntryCell
                  entry={entries[coord]}
                  label={Object.values(table.places?.[r]?.[c] ?? {}).join(' ')}
                  r={r}
                  c={c}
                  pending={pending.has(coord)}
                  onCommit={(text, entry) => send([{ coord, text, entry }])}
                  onPaste={paste(r, c)}
                />
              )
            }}
          />
          {editable && (
            <p className="muted small">
              {editable.name}의 칸을 여기서 고칠 수 있습니다. 합치기를 쓰면 고칠 수 없습니다.
            </p>
          )}
        </>
      )}
      <Sheet open={settings} onClose={() => setSettings(false)} title="보기 설정">
        {settings && <ViewSettings key={view.version} cur={cur} view={view} onClose={() => setSettings(false)} />}
      </Sheet>
    </div>
  )
}

/** /views/<id> */
export function SavedViewScreen({ cur, id }: { cur: Current; id: Id }) {
  const view = cur.book?.views[id]
  if (view && !view.deleted) return <ViewEditor key={view.id} cur={cur} view={view} />
  if (cur.book?.loading) return <Spinner />
  return (
    <div className="page t-page">
      <p className="muted empty-note">없는 보기입니다.</p>
      <button type="button" className="secondary" onClick={() => navigate('/views', { replace: true })}>
        보기 목록
      </button>
    </div>
  )
}
